'use client';
import React, { useMemo, useRef, useState, useTransition } from 'react';
import { useLocale } from 'use-intl';
import {
  fetchConferenceChatAuthInfo,
  getConferenceChatAuthApiBySite,
  isConferenceChatGuestSelf,
  openConferenceChatEntry,
} from '../../../navbar/conferenceChat';
import type { ConferenceChatAuthInfo } from '../../../navbar/conferenceChat';
import Image from '../../../image';
import SectionTitle from './SectionTitle';
import SectionYearSelector from './SectionYearSelector';
import styles from './GuestsSection.module.css';
import { fetchConferenceGuestsByYear } from '../api';
import type {
  ConferenceSite,
  ConferenceGuestGroupItem,
  ConferenceGuestGroupListResp,
  ConferenceGuestListItem,
} from '../types';

interface GuestsSectionProps {
  data?: ConferenceGuestGroupListResp;
  title?: string;
  site?: ConferenceSite;
  years?: number[];
}

const MOBILE_GUEST_PAGE_SIZE = 6;
const MOBILE_PAGER_TRACK_WIDTH_REM = 1.5;
const TABS_SCROLL_EDGE_TOLERANCE = 2;

interface TabsScrollState {
  canScrollLeft: boolean;
  canScrollRight: boolean;
}

const DEFAULT_TABS_SCROLL_STATE: TabsScrollState = {
  canScrollLeft: false,
  canScrollRight: false,
};

const chunkList = <T,>(list: T[], size: number) => {
  const pages: T[][] = [];
  for (let index = 0; index < list.length; index += size) {
    pages.push(list.slice(index, index + size));
  }
  return pages;
};

const getGuestName = (guest: ConferenceGuestListItem, isZh: boolean) =>
  (isZh ? guest.NameCN || guest.NameEN : guest.NameEN || guest.NameCN) || '-';

const isGuestContactAllowed = (guest?: ConferenceGuestListItem) =>
  (guest?.AllowContact ?? guest?.allowContact ?? 1) !== 0;

const normalizeGuestTitle = (title?: string) => {
  const value = String(title || '').trim();
  return value;
};

const getGuestTitles = (guest: ConferenceGuestListItem, isZh: boolean) => {
  const titles = guest.Titles || [];
  const titleList = titles
    // 嘉宾头衔按当前语言独立展示，缺少当前语言内容时不跨语言兜底。
    .map((item) => normalizeGuestTitle(isZh ? item.TitleCN : item.TitleEN))
    .filter(Boolean);
  return titleList;
};

const getGroupTitle = (group: ConferenceGuestGroupItem, isZh: boolean) =>
  normalizeGuestTitle(isZh ? group.TitleCN : group.TitleEN) || '-';

const getGroupSubtitle = (group: ConferenceGuestGroupItem, isZh: boolean) =>
  normalizeGuestTitle(isZh ? group.SubtitleCN : group.SubtitleEN);

const clampScrollLeft = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const GuestsTabsArrowIcon = ({
  direction,
}: {
  direction: 'left' | 'right';
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="6"
    height="12"
    viewBox="0 0 8 14"
    fill="none"
    aria-hidden="true"
    style={{
      width: '6px',
      height: '12px',
      transform: direction === 'left' ? 'rotate(180deg)' : undefined,
    }}
  >
    <path
      d="M0.636719 0.63623L6.63672 6.63623L0.636719 12.6362"
      stroke="#121C58"
      strokeWidth="1.8"
    />
  </svg>
);

const normalizeTabsScrollLeft = (value: number, maxScrollLeft: number) => {
  if (maxScrollLeft <= TABS_SCROLL_EDGE_TOLERANCE) {
    return 0;
  }

  const nextValue = clampScrollLeft(value, 0, maxScrollLeft);
  if (nextValue <= TABS_SCROLL_EDGE_TOLERANCE) {
    return 0;
  }
  if (maxScrollLeft - nextValue <= TABS_SCROLL_EDGE_TOLERANCE) {
    return maxScrollLeft;
  }
  return nextValue;
};

const getTabsTargetScrollLeft = (
  tabsWrap: HTMLDivElement,
  direction: 'left' | 'right'
) => {
  const maxScrollLeft = tabsWrap.scrollWidth - tabsWrap.clientWidth;
  if (maxScrollLeft <= TABS_SCROLL_EDGE_TOLERANCE) {
    return 0;
  }

  const containerRect = tabsWrap.getBoundingClientRect();
  const tabs = Array.from(tabsWrap.children).filter(
    (child): child is HTMLElement => child instanceof HTMLElement
  );
  const tabRects = tabs.map((tab) => {
    const rect = tab.getBoundingClientRect();
    const left = rect.left - containerRect.left + tabsWrap.scrollLeft;
    return {
      left,
      right: left + rect.width,
      width: rect.width,
    };
  });

  if (direction === 'right') {
    const visibleRight = tabsWrap.scrollLeft + tabsWrap.clientWidth;
    const targetIndex = tabRects.findIndex(
      (tabRect) => tabRect.right > visibleRight + TABS_SCROLL_EDGE_TOLERANCE
    );
    if (targetIndex === -1 || targetIndex === tabRects.length - 1) {
      return maxScrollLeft;
    }

    const targetTab = tabRects[targetIndex];
    const nextTab = tabRects[targetIndex + 1];
    const desiredVisibleRight = nextTab.left + nextTab.width / 2;
    const minScrollLeft = targetTab.right - tabsWrap.clientWidth;
    const maxScrollLeftForTarget = targetTab.left;
    const targetScrollLeft = clampScrollLeft(
      desiredVisibleRight - tabsWrap.clientWidth,
      minScrollLeft,
      maxScrollLeftForTarget
    );

    return normalizeTabsScrollLeft(targetScrollLeft, maxScrollLeft);
  }

  const visibleLeft = tabsWrap.scrollLeft;
  let targetIndex = -1;
  for (let index = tabRects.length - 1; index >= 0; index -= 1) {
    if (tabRects[index].left < visibleLeft - TABS_SCROLL_EDGE_TOLERANCE) {
      targetIndex = index;
      break;
    }
  }

  if (targetIndex <= 0) {
    return 0;
  }

  const targetTab = tabRects[targetIndex];
  const previousTab = tabRects[targetIndex - 1];
  const desiredVisibleLeft = previousTab.right - previousTab.width / 2;
  const minScrollLeft = targetTab.right - tabsWrap.clientWidth;
  const maxScrollLeftForTarget = targetTab.left;
  const targetScrollLeft = clampScrollLeft(
    desiredVisibleLeft,
    minScrollLeft,
    maxScrollLeftForTarget
  );

  return normalizeTabsScrollLeft(targetScrollLeft, maxScrollLeft);
};

const shouldUseCompactTitle = (titles: string[], isZh: boolean) => {
  if (!isZh || titles.length === 0) {
    return false;
  }

  const normalizedLength = titles.join('').replace(/\s+/g, '').length;
  // 中文 PC 极限值仅用于长头衔或多条头衔，避免把 11px 样式误套到常规卡片。
  return (
    titles.length >= 3 ||
    normalizedLength > 28 ||
    titles.some((title) => title.length > 18)
  );
};

const buildGuestHoverKey = (guest?: ConferenceGuestListItem) =>
  `${guest?.GuestDataSource || 'cn'}-${guest?.GuestID || ''}-${
    guest?.RelationID || ''
  }`;

function GuestChatIcon() {
  return (
    <svg
      aria-hidden="true"
      className={styles.chatEntryIcon}
      width="18"
      height="16"
      viewBox="0 0 18 16"
      fill="none"
    >
      <path
        d="M0 1.44C0 0.644752 0.644752 0 1.44 0H16.56C17.3552 0 18 0.644752 18 1.44V11.2039C18 11.9991 17.3552 12.6439 16.56 12.6439H3.36667C3.20396 12.6439 3.04609 12.699 2.91868 12.8003L0 15.12V1.44Z"
        className={styles.chatEntryBubble}
      />
      <circle
        cx="4.68352"
        cy="6.1044"
        r="1.08"
        className={styles.chatEntryDot}
      />
      <circle
        cx="9.00774"
        cy="6.1044"
        r="1.08"
        className={styles.chatEntryDot}
      />
      <circle
        cx="13.3261"
        cy="6.1044"
        r="1.08"
        className={styles.chatEntryDot}
      />
    </svg>
  );
}

/**
 * 大会嘉宾组件
 */
export default function GuestsSection({
  data,
  title,
  site = 'zh',
  years,
}: GuestsSectionProps) {
  const locale = useLocale();
  const isZh = locale === 'zh';
  const showOrderHint = site === 'zh';
  const authApi = getConferenceChatAuthApiBySite(site);
  const [, startTransition] = useTransition();
  const requestIdRef = useRef(0);
  const [sectionData, setSectionData] = useState<ConferenceGuestGroupListResp>(
    data || { list: [] }
  );
  const [selectedYear, setSelectedYear] = useState(
    () =>
      years?.[0] ||
      data?.list?.find((group) => group.ConferenceYear)?.ConferenceYear ||
      new Date().getFullYear()
  );
  const groups = useMemo(() => sectionData?.list || [], [sectionData]);
  const [chatAuth, setChatAuth] = useState<ConferenceChatAuthInfo>();
  const [activeGroupID, setActiveGroupID] = useState('');
  const [mobileScrollLeft, setMobileScrollLeft] = useState(0);
  const [tabsScrollState, setTabsScrollState] = useState<TabsScrollState>(
    DEFAULT_TABS_SCROLL_STATE
  );
  const [suppressedHoverGuestKey, setSuppressedHoverGuestKey] = useState('');
  const tabsRef = useRef<HTMLDivElement>(null);
  const tabsScrollStateRef = useRef(tabsScrollState);
  const mobilePagerRef = useRef<HTMLDivElement>(null);
  const hasGuests = groups.some((group) => (group.Guests || []).length > 0);
  const activeGroup =
    groups.find((item) => item.GroupID === activeGroupID) || groups[0];
  const guests = useMemo(() => activeGroup?.Guests || [], [activeGroup]);
  // 移动端设计要求每页 2 列 x 3 行展示，前端只做展示切片，不改变接口排序。
  const mobileGuestPages = useMemo(
    () => chunkList(guests, MOBILE_GUEST_PAGE_SIZE),
    [guests]
  );
  const yearOptions = useMemo(() => years || [], [years]);

  React.useEffect(() => {
    setSectionData(data || { list: [] });
    if (years?.[0]) {
      setSelectedYear(years[0]);
    }
  }, [data, years]);

  const handleYearChange = (year: number) => {
    if (year === selectedYear) {
      return;
    }
    setSelectedYear(year);
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    startTransition(() => {
      void fetchConferenceGuestsByYear({
        conferenceYear: year,
        site,
      }).then((nextData) => {
        if (requestIdRef.current === requestId) {
          setSectionData(nextData);
        }
      });
    });
  };

  React.useEffect(() => {
    setActiveGroupID(groups[0]?.GroupID || '');
  }, [groups]);

  React.useEffect(() => {
    tabsScrollStateRef.current = tabsScrollState;
  }, [tabsScrollState]);

  React.useEffect(() => {
    let frameID = 0;
    let resizeObserver: ResizeObserver | undefined;

    const commitState = (nextState: TabsScrollState) => {
      const previousState = tabsScrollStateRef.current;
      if (
        previousState.canScrollLeft === nextState.canScrollLeft &&
        previousState.canScrollRight === nextState.canScrollRight
      ) {
        return;
      }

      tabsScrollStateRef.current = nextState;
      setTabsScrollState(nextState);
    };

    const updateTabsScrollState = () => {
      frameID = 0;

      const tabs = tabsRef.current;
      if (!tabs) {
        commitState(DEFAULT_TABS_SCROLL_STATE);
        return;
      }

      const maxScrollLeft = tabs.scrollWidth - tabs.clientWidth;
      const hasOverflow = maxScrollLeft > 1;
      const scrollLeft = normalizeTabsScrollLeft(
        tabs.scrollLeft,
        maxScrollLeft
      );

      commitState({
        canScrollLeft: hasOverflow && scrollLeft > 0,
        canScrollRight: hasOverflow && scrollLeft < maxScrollLeft,
      });
    };

    const scheduleUpdate = () => {
      if (frameID) return;
      frameID = window.requestAnimationFrame(updateTabsScrollState);
    };

    const tabs = tabsRef.current;
    scheduleUpdate();
    window.addEventListener('resize', scheduleUpdate);
    tabs?.addEventListener('scroll', scheduleUpdate, { passive: true });

    if (tabs && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(scheduleUpdate);
      resizeObserver.observe(tabs);
    }

    return () => {
      if (frameID) {
        window.cancelAnimationFrame(frameID);
      }
      window.removeEventListener('resize', scheduleUpdate);
      tabs?.removeEventListener('scroll', scheduleUpdate);
      resizeObserver?.disconnect();
    };
  }, [groups]);

  React.useEffect(() => {
    let cancelled = false;
    const loadChatAuth = async () => {
      try {
        const payload = await fetchConferenceChatAuthInfo(authApi);
        if (!cancelled && !payload.loginRequired) {
          setChatAuth(payload.data);
        }
      } catch (error) {
        if (!cancelled) {
          setChatAuth(undefined);
        }
      }
    };
    void loadChatAuth();
    return () => {
      cancelled = true;
    };
  }, [authApi]);

  React.useEffect(() => {
    setMobileScrollLeft(0);
    mobilePagerRef.current?.scrollTo({ left: 0 });
  }, [activeGroup?.GroupID]);

  const getMobileIndicatorLeft = () => {
    const pager = mobilePagerRef.current;
    if (!pager) {
      return 0;
    }

    const scrollableWidth = pager.scrollWidth - pager.clientWidth;
    if (scrollableWidth <= 0) {
      return -1;
    }

    const indicatorWidth =
      MOBILE_PAGER_TRACK_WIDTH_REM / mobileGuestPages.length;
    const maxOffset = MOBILE_PAGER_TRACK_WIDTH_REM - indicatorWidth;
    const ratio = Math.min(1, Math.max(0, mobileScrollLeft / scrollableWidth));
    return maxOffset * ratio;
  };

  const handleMobilePagerScroll = () => {
    const pager = mobilePagerRef.current;
    if (!pager) {
      return;
    }
    setMobileScrollLeft(pager.scrollLeft);
  };

  const handleTabsScrollButtonClick = (direction: 'left' | 'right') => {
    const tabs = tabsRef.current;
    if (!tabs) return;

    tabs.scrollTo({
      left: getTabsTargetScrollLeft(tabs, direction),
      behavior: 'smooth',
    });
  };

  const handleOpenConferenceChat = (
    event: React.MouseEvent<HTMLButtonElement>,
    guest?: ConferenceGuestListItem
  ) => {
    event.preventDefault();
    event.stopPropagation();
    // 新标签打开后当前页不会立刻触发 mouseleave，先临时压住 hover，
    // 避免用户切回来时卡片仍停留在悬浮态；待鼠标再次移动/离开后恢复。
    setSuppressedHoverGuestKey(buildGuestHoverKey(guest));
    event.currentTarget.blur();
    if (
      isConferenceChatGuestSelf(chatAuth, {
        contactId: guest?.GuestID,
        guestDataSource: guest?.GuestDataSource,
      })
    ) {
      return;
    }
    void openConferenceChatEntry({
      authApi,
      site,
      // 大会嘉宾卡片进入会聊时保持当前大会页上下文，统一以新标签打开会聊详情。
      openInNewTab: true,
      targetSearchParams:
        guest?.GuestID && guest?.GuestDataSource
          ? {
              tab: 'messages',
              contactId: guest.GuestID,
              guestDataSource: guest.GuestDataSource,
            }
          : undefined,
    });
  };

  const resetSuppressedHoverGuest = (guest?: ConferenceGuestListItem) => {
    const hoverKey = buildGuestHoverKey(guest);
    setSuppressedHoverGuestKey((currentKey) =>
      currentKey === hoverKey ? '' : currentKey
    );
  };

  // 嘉宾卡片会聊入口仅在嘉宾允许站内信联系时展示；登录态和本人判断仍由会聊鉴权兜底。
  const renderChatEntry = (
    placement: 'avatar' | 'name',
    guest: ConferenceGuestListItem
  ) => {
    return (
      <span
        className={`${styles.chatEntryWrap} ${
          placement === 'avatar'
            ? styles.chatEntryWrapAvatar
            : styles.chatEntryWrapName
        }`}
      >
        <button
          type="button"
          className={styles.chatEntry}
          onClick={(event) => handleOpenConferenceChat(event, guest)}
          aria-label={isZh ? '和嘉宾聊天' : 'Chat with guest'}
        >
          <GuestChatIcon />
        </button>
        {placement === 'avatar' && (
          <span className={styles.chatTooltip} role="tooltip">
            {isZh ? '和嘉宾聊天' : 'Chat with guest'}
          </span>
        )}
      </span>
    );
  };

  const renderGuestCard = (guest: ConferenceGuestListItem) => {
    const hoverKey = buildGuestHoverKey(guest);
    const isHoverSuppressed = suppressedHoverGuestKey === hoverKey;
    const guestName = getGuestName(guest, isZh);
    // 设计极限态按最多 3 条头衔展示，中文长头衔在单条内省略，避免挤占其它头衔。
    const guestTitles = getGuestTitles(guest, isZh).slice(0, 3);
    const useCompactTitle = shouldUseCompactTitle(guestTitles, isZh);
    const showChatEntry =
      isGuestContactAllowed(guest) &&
      !isConferenceChatGuestSelf(chatAuth, {
        contactId: guest.GuestID,
        guestDataSource: guest.GuestDataSource,
      });
    return (
      <article
        key={`${guest.GuestDataSource || 'cn'}-${guest.GuestID}-${
          guest.RelationID
        }`}
        className={`${styles.guestCard} ${
          isHoverSuppressed ? styles.guestCardHoverSuppressed : ''
        }`}
        onMouseMove={() => resetSuppressedHoverGuest(guest)}
        onMouseLeave={() => resetSuppressedHoverGuest(guest)}
      >
        <div
          className={`${styles.avatarWrap} ${
            isHoverSuppressed ? styles.avatarWrapHoverSuppressed : ''
          }`}
        >
          <div className={styles.avatarMedia}>
            {guest.Avatar ? (
              <Image
                src={guest.Avatar}
                alt={guestName}
                className={styles.avatar}
                loading="lazy"
              />
            ) : (
              <div className={styles.avatarFallback}>-</div>
            )}
          </div>
          {showChatEntry && renderChatEntry('avatar', guest)}
        </div>
        <div className={styles.guestInfo}>
          <div className={styles.guestNameRow}>
            <h3>{guestName}</h3>
            {showChatEntry && renderChatEntry('name', guest)}
          </div>
          {guestTitles.length > 0 && (
            <p
              className={`${styles.guestTitle} ${
                useCompactTitle ? styles.guestTitleCompact : ''
              }`}
            >
              {guestTitles.map((title, index) => (
                <span
                  key={`${
                    guest.GuestID || guest.RelationID || guestName
                  }-title-${index}`}
                >
                  {title}
                </span>
              ))}
            </p>
          )}
        </div>
      </article>
    );
  };

  // 无数据时仍保留模块标题和锚点，保证顶部导航可以跳转到大会嘉宾模块。
  const mobileIndicatorLeft = getMobileIndicatorLeft();

  return (
    <section
      id="guests"
      className={`${styles.section} ${isZh ? '' : styles.sectionEn}`}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerMain}>
            <div className={styles.titleRow}>
              <SectionTitle className={styles.sectionTitle}>
                {title}
              </SectionTitle>
              {showOrderHint ? (
                <span
                  className={`${styles.orderHint} ${styles.orderHintDesktop}`}
                >
                  按照发言次序排列
                </span>
              ) : null}
            </div>
            <SectionYearSelector
              selectedYear={selectedYear}
              years={yearOptions}
              onChange={handleYearChange}
            />
          </div>
          {showOrderHint ? (
            <span className={`${styles.orderHint} ${styles.orderHintMobile}`}>
              按照发言次序排列
            </span>
          ) : null}
        </div>
        {hasGuests ? (
          <>
            <div className={styles.tabsWrap}>
              <div ref={tabsRef} className={styles.tabs}>
                {groups.map((group) => (
                  <button
                    key={group.GroupID}
                    className={`${styles.tabButton} ${
                      group.GroupID === activeGroup?.GroupID
                        ? styles.tabButtonActive
                        : ''
                    }`}
                    onClick={() => setActiveGroupID(group.GroupID || '')}
                    type="button"
                  >
                    <span>{getGroupTitle(group, isZh)}</span>
                    {getGroupSubtitle(group, isZh) && (
                      <small>{getGroupSubtitle(group, isZh)}</small>
                    )}
                  </button>
                ))}
              </div>
              {tabsScrollState.canScrollLeft && (
                <>
                  <span
                    className={`${styles.tabsFade} ${styles.tabsFadeLeft}`}
                    aria-hidden="true"
                  />
                  <button
                    type="button"
                    className={`${styles.tabsArrow} ${styles.tabsArrowLeft}`}
                    onClick={() => handleTabsScrollButtonClick('left')}
                    aria-label={
                      isZh ? '向左滚动嘉宾分类' : 'Scroll guest tabs left'
                    }
                  >
                    <GuestsTabsArrowIcon direction="left" />
                  </button>
                </>
              )}
              {tabsScrollState.canScrollRight && (
                <>
                  <span
                    className={`${styles.tabsFade} ${styles.tabsFadeRight}`}
                    aria-hidden="true"
                  />
                  <button
                    type="button"
                    className={`${styles.tabsArrow} ${styles.tabsArrowRight}`}
                    onClick={() => handleTabsScrollButtonClick('right')}
                    aria-label={
                      isZh ? '向右滚动嘉宾分类' : 'Scroll guest tabs right'
                    }
                  >
                    <GuestsTabsArrowIcon direction="right" />
                  </button>
                </>
              )}
            </div>
            <div className={styles.tabsBorder}></div>

            <div className={styles.guestGrid}>
              {guests.map((guest) => renderGuestCard(guest))}
            </div>

            <div
              ref={mobilePagerRef}
              className={styles.mobileGuestPager}
              onScroll={handleMobilePagerScroll}
            >
              {mobileGuestPages.map((pageGuests, pageIndex) => (
                <div className={styles.mobileGuestPage} key={pageIndex}>
                  {pageGuests.map((guest) => renderGuestCard(guest))}
                </div>
              ))}
            </div>
            {mobileGuestPages.length > 1 && mobileIndicatorLeft >= 0 && (
              <div className={styles.mobilePageIndicator} aria-hidden="true">
                <div className={styles.mobilePageIndicatorTrack}>
                  <span
                    className={styles.mobilePageIndicatorActive}
                    style={{
                      left: `${mobileIndicatorLeft}rem`,
                      width: `${
                        MOBILE_PAGER_TRACK_WIDTH_REM / mobileGuestPages.length
                      }rem`,
                    }}
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className={styles.empty}>{isZh ? '暂无嘉宾' : 'No guests'}</div>
        )}
      </div>
    </section>
  );
}
